import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Text,
} from '@mantine/core';
import { useEffect, useRef } from 'react';
import { PiArrowLeft, PiDownload } from 'react-icons/pi';
import { useParams } from 'wouter';
import { useCommercialProposal } from '@/models/commercial-proposal';
import { useProposalAccess } from '@/models/permissions';

export default function CommercialProposalViewPage() {
  const { id } = useParams();
  const { data: proposal, isLoading } = useCommercialProposal(Number(id));
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { canView: gotAccess } = useProposalAccess();

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when proposal loads
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const onLoad = () => {
      try {
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow?.document;

        setTimeout(() => {
          if (!iframeDocument) return;
          const body = iframeDocument.body;
          const height = body.scrollHeight;
          iframe.style.height = height + 'px';
          iframe.style.visibility = 'visible';
        }, 100);
      } catch (error) {
        console.warn('Cannot access iframe content:', error);
      }
    };

    iframe.addEventListener('load', onLoad);

    return () => {
      iframe.removeEventListener('load', onLoad);
    };
  }, [proposal, isLoading, gotAccess]);

  if (!gotAccess) {
    return (
      <Box p="xl">
        <Text>You do not have permission to view this proposal.</Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        p="xl"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Loader size="lg" />
      </Box>
    );
  }

  if (!proposal) {
    return (
      <Box p="xl">
        <Text>Proposal not found</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex
        p="md"
        align="center"
        gap="md"
        justify={'space-between'}
        style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Group>
          <ActionIcon variant="subtle" onClick={() => history.back()} size="lg">
            <PiArrowLeft size={20} />
          </ActionIcon>
          <Text fw={500}>{proposal.name}</Text>
        </Group>
        <Button
          component="a"
          href={`/api/backoffice/commercial-proposal/download-pdf?uuid=${proposal.uuid}`}
          target="_blank"
          variant="light"
          size="xs"
          leftSection={<PiDownload size={16} />}
        >
          Download PDF
        </Button>
      </Flex>

      <Box style={{ overflow: 'hidden' }}>
        <iframe
          ref={iframeRef}
          src={`/api/backoffice/commercial-proposal/preview?uuid=${proposal.uuid}`}
          style={{
            width: '100%',
            height: `600px`,
            border: 'none',
            display: 'block',
            overflow: 'hidden',
            visibility: 'hidden',
          }}
          title="Commercial Proposal Preview"
        />
      </Box>
    </Box>
  );
}
